import type { ApiEndpointsController } from '../../hooks/useApiEndpoints';
import { HealthBanner } from '../../components/ui/HealthBanner';
import { ErrorCard, LoadingCard } from '../../components/ui/ScreenFallback';
import { Toast, useToast } from '../../components/ui/Toast';
import { DependencyRow } from './DependencyRow';

/**
 * Tela de Status: leitura da saúde de tudo que o SICOP depende para funcionar.
 * Mesma fonte de dados da Administração, sem as ações de cadastro — quem entra
 * aqui quer saber o que está fora do ar, não mexer na lista.
 */
export function StatusScreen({ controller }: { controller: ApiEndpointsController }) {
  const { state, loading, error, checking } = controller;
  const { toast, show } = useToast();

  const handleCheckNow = async () => {
    try {
      await controller.checkNow();
      show('Verificação concluída.');
    } catch (cause) {
      show((cause as Error).message, 'error');
    }
  };

  if (loading) {
    return <LoadingCard message="Carregando o status das dependências…" />;
  }

  if (error || !state) {
    return (
      <ErrorCard
        title="Não foi possível carregar o status das dependências."
        detail={error}
        onRetry={() => void controller.reload()}
      />
    );
  }

  // O cron só verifica as APIs ativas, então o resumo e a lista falam do mesmo.
  const monitored = state.apis.filter((api) => api.active);
  const paused = state.apis.length - monitored.length;

  return (
    <>
      <HealthBanner state={state} checking={checking} onCheckNow={() => void handleCheckNow()} />

      <div className="border-line-200 overflow-hidden rounded-2xl border bg-white">
        {monitored.length === 0 ? (
          <div className="text-ink-600 p-7 text-center text-[13.5px]">
            Nenhuma dependência monitorada. Cadastre as URLs em Administração.
          </div>
        ) : (
          monitored.map((api) => <DependencyRow key={api.id} api={api} />)
        )}
      </div>

      {paused > 0 ? (
        <div className="text-ink-400 mt-2.5 text-[11.5px]">
          {paused === 1
            ? '1 dependência inativa não é verificada e ficou fora desta lista.'
            : `${paused} dependências inativas não são verificadas e ficaram fora desta lista.`}
        </div>
      ) : null}

      <Toast message={toast} />
    </>
  );
}
